import React, { ReactNode, useEffect } from 'react';
import type { Poll } from 'schema/poll';
import { ForbiddenError } from 'src/components/ForbiddenError';
import { NewForbiddenErrorOverlay } from 'src/components/NewForbiddenErrorOverlay';
import Completed from '../../components/Completed';
import { GlobalTimerOverlay } from '../../components/GlobalTimerOverlay';
import { LoaderOverlay } from '../../components/LoaderOverlay';
import { NewCompletedOverlay } from '../../components/NewCompletedOverlay';
import { NewConfigurationErrorOverlay } from '../../components/NewConfigurationErrorOverlay';
import { NewGlobalTimerOverlay } from '../../components/NewGlobalTimerOverlay/NewGlobalTimerOverlay';
import { NewLoaderOverlay } from '../../components/NewLoaderOverlay';
import { NewPassedOverlay } from '../../components/NewPassedOverlay';
import { OpeningError } from '../../components/OpeningError';
import Passed from '../../components/Passed';
import { usePollStatePredicate } from '../../reducer/machine/utils';
import { notifyIframeHeight } from '../../utils/notifyIframeHeight';
import { isWebviewTemplate } from '../../utils/template';
import { useICStateActions } from './ICStateActionsContext';

export function PollOverlays({ poll, children }: { poll: Poll; children: ReactNode }) {
  const { onClose, onStartGlobalTimer } = useICStateActions();

  const isWebview = isWebviewTemplate(poll.template);

  const systemScreens = poll.systemScreens ?? [];

  const isWrongConfiguration = usePollStatePredicate('wrongConfiguration');
  const isGlobalTimerWelcome = usePollStatePredicate('globalTimerWelcome');
  const isPassedPoll = usePollStatePredicate('passedPoll');
  const isCompletedPoll = usePollStatePredicate('completedPoll');
  const isForbiddenError = usePollStatePredicate('forbiddenError');
  const closed = usePollStatePredicate('closed');

  useEffect(() => {
    if (closed || isWrongConfiguration || isPassedPoll || isCompletedPoll || isGlobalTimerWelcome || isForbiddenError) {
      notifyIframeHeight();
    }
  }, [isWrongConfiguration, isForbiddenError, isPassedPoll, isCompletedPoll, closed, isGlobalTimerWelcome]);

  if (isForbiddenError) {
    if (poll.template === 'poll') {
      return <NewForbiddenErrorOverlay onClose={onClose} isWebview={isWebview} />;
    }
    return <ForbiddenError isWebview={isWebview} onClose={onClose} />;
  }

  if (closed) {
    if (poll.template === 'poll') {
      return <NewLoaderOverlay isWebview={isWebview} />;
    }
    return <LoaderOverlay isWebview={isWebview} />;
  }

  if (isWrongConfiguration) {
    if (poll.template === 'poll') {
      return (
        <NewConfigurationErrorOverlay
          isWebview={isWebview}
          onClose={onClose}
          overrideData={systemScreens.find(screen => screen.data.cause === 'INCORRECT_CONFIGURATION')}
        />
      );
    }
    return <OpeningError isWebview={isWebview} onClose={onClose} />;
  }

  if (isPassedPoll) {
    if (poll.template === 'poll') {
      return (
        <NewPassedOverlay
          isWebview={isWebview}
          onClose={onClose}
          overrideData={systemScreens.find(screen => screen.data.cause === 'COMPLETED')}
        />
      );
    }
    return <Passed isWebview={isWebview} onClose={onClose} />;
  }

  if (isCompletedPoll) {
    if (poll.template === 'poll') {
      return (
        <NewCompletedOverlay
          isWebview={isWebview}
          onClose={onClose}
          overrideData={systemScreens.find(screen => screen.data.cause === 'WITHDRAWN')}
        />
      );
    }
    return <Completed isWebview={isWebview} onClose={onClose} />;
  }

  if (isGlobalTimerWelcome) {
    if (poll.template === 'poll') {
      return (
        <NewGlobalTimerOverlay
          onStartGlobalTimer={onStartGlobalTimer}
          timerStrict={poll.timerStrict}
          timeDurationSec={poll.timerDuration}
          onClose={onClose}
          isWebview={isWebview}
        />
      );
    }
    return (
      <GlobalTimerOverlay
        onStartGlobalTimer={onStartGlobalTimer}
        timerStrict={poll.timerStrict}
        timeDurationSec={poll.timerDuration}
      />
    );
  }

  return children;
}
